import { connect } from 'react-redux'

import { filtersActions, INCOME_LIST_INITIAL_FILTERS } from '@services'

import { usePageConfig } from '@contexts/PageControllerProvider'
import { usePortal } from '@contexts/PortalProvider'

import { INCOME_LIST_DATE_FIELD_OPTIONS } from '@components/pages/constants'

import { ALL_INCOME_SEARCH_FILTERS, DEFAULT_INVOICE_SEARCH_FILTERS, StatusFilters } from '@constants'

import type { FilterBarBaseProps } from './FilterBar'
import { FilterBar } from './FilterBar'
import {
  checkFiltersChanged,
  getSelectableFilterProps,
  getStatusFilterDialogProps,
  getStatusFilterProps,
} from './helpers'

import {
  AllFilterOptionsMessage,
  DefaultFilterOptionsMessage,
  IncomeListPageMessage,
  NewStatusMessage,
  NoArtifactStatusMessage,
  TagFilterEmptyPlaceholderMessage,
  TagFilterLabelMessage,
  UncategorizedStatusMessage,
} from '@messages'

interface PureIncomeListPageFilterBarProps extends FilterBarBaseProps {
  filters: IncomeListStoreFilters
  paymentMethodOptions: PaymentMethod[]
  revenueTypeOptions: CommonIdAndNameType[]
  tagOptions: Tag[]
  vatAreaOptions: VatArea[]
}

function PureIncomeListPageFilterBar({
  revenueTypeOptions,
  filters,
  filters: {
    dateField,
    expiredDays,
    expiringDays,
    isExpired,
    isExpiring,
    isPaid,
    origin,
    paymentMethod,
    search,
    searchFields,
    vatArea,
    withRevenueTypeIds,
    withoutRevenueTypeIds,
    withoutTagIds,
    withTagIds,
  },
  isDateFilterDisabled,
  paymentMethodOptions,
  resetFilters,
  tagOptions,
  updateFilters,
  vatAreaOptions,
}: PureIncomeListPageFilterBarProps) {
  const { setPortalAnchorEl } = usePortal()
  const pageConfig = usePageConfig()

  const isDateFilterUsed = true
  const paidStatusFilterProps = {
    values: {
      expiredDays,
      expiringDays,
      isExpired,
      isExpiring,
      isPaid,
    },
  }
  const searchFilterProps = {
    search,
    searchFields,
    options: pageConfig.searchFilterProps?.config.options ?? [],
    extraOptions: pageConfig.searchFilterProps?.config.extraOptions ?? [],
    presets: [
      {
        key: 'defaults',
        optionKeys: DEFAULT_INVOICE_SEARCH_FILTERS,
        label: DefaultFilterOptionsMessage,
      },
      {
        key: 'all',
        optionKeys: ALL_INCOME_SEARCH_FILTERS,
        label: AllFilterOptionsMessage,
      },
    ],
  }

  const selectableFilterProps = getSelectableFilterProps({
    values: {
      withTagIds,
    },
    config: [
      {
        keyValue: 'withTagIds' as const,
        options: tagOptions,
        labelText: TagFilterLabelMessage,
        emptyPlaceholder: TagFilterEmptyPlaceholderMessage,
      },
    ],
  })

  const statusFilterProps = getStatusFilterProps({
    config: [
      {
        name: StatusFilters.IS_NEW,
        options: [{ value: true, label: NewStatusMessage }],
      },
      {
        name: StatusFilters.HAS_ARTIFACT,
        options: [{ value: false, label: NoArtifactStatusMessage }],
      },
      {
        name: StatusFilters.HAS_REVENUE_TYPE,
        options: [{ value: false, label: UncategorizedStatusMessage }],
      },
    ],
    values: filters,
    width: 421,
  })

  const filterDialogProps: FilterDialogProps<StatusFilterConfig> = {
    dateFilterProps: {
      dateTypeOptions: INCOME_LIST_DATE_FIELD_OPTIONS,
      dateTypeValue: dateField,
    },
    isChanged: checkFiltersChanged(filters, INCOME_LIST_INITIAL_FILTERS),
    page: IncomeListPageMessage,
    paidStatusFilterProps,
    searchFilterProps,
  }

  if (pageConfig.statusFilterProps) {
    filterDialogProps['statusFilterProps'] = getStatusFilterDialogProps({
      config: pageConfig.statusFilterProps.config,
      values: filters,
    })
  }
  if (pageConfig.categoryFilterProps) {
    filterDialogProps['categoryFilterProps'] = {
      config: {
        ...pageConfig.categoryFilterProps.config,
        options: revenueTypeOptions,
      },
      values: {
        include: withRevenueTypeIds,
        exclude: withoutRevenueTypeIds,
      },
    }
  }
  if (pageConfig.tagFilterProps) {
    filterDialogProps['tagFilterProps'] = {
      config: {
        ...pageConfig.tagFilterProps.config,
        options: tagOptions,
      },
      values: {
        include: withTagIds,
        exclude: withoutTagIds,
      },
    }
  }
  if (pageConfig.currencyFilterProps) {
    filterDialogProps['currencyFilterProps'] = {
      // config: pageConfig.currencyFilterProps.config,
      currencyId: filters.currencyId, // TODO need to refact: { config: { name: 'currency', options: currencyOptions }, values: filters.currency }
    }
  }
  if (pageConfig.amountFilterProps) {
    filterDialogProps['amountFilterProps'] = {
      // config: pageConfig.amountFilterProps.config,
      grossAmountMax: filters.grossAmountMax,
      grossAmountMin: filters.grossAmountMin,
    }
  }
  if (pageConfig.originFilterProps) {
    filterDialogProps['originFilterProps'] = {
      config: pageConfig.originFilterProps.config,
      values: origin,
    }
  }
  if (pageConfig.paymentMethodFilterProps) {
    filterDialogProps['paymentMethodFilterProps'] = {
      config: {
        ...pageConfig.paymentMethodFilterProps.config,
        options: paymentMethodOptions,
      },
      values: paymentMethod,
    }
  }
  if (pageConfig.vatAreaFilterProps) {
    filterDialogProps['vatAreaFilterProps'] = {
      config: {
        ...pageConfig.vatAreaFilterProps.config,
        options: vatAreaOptions,
      },
      values: vatArea,
    }
  }

  return (
    <FilterBar
      dateField={filters.dateField}
      filterDialogProps={filterDialogProps}
      isDateFilterDisabled={isDateFilterDisabled}
      isDateFilterUsed={isDateFilterUsed}
      paidStatusFilterProps={paidStatusFilterProps}
      resetFilters={resetFilters}
      searchFilterProps={searchFilterProps}
      selectableFilterProps={selectableFilterProps}
      setPortalRef={setPortalAnchorEl}
      statusFilterProps={statusFilterProps}
      updateFilters={updateFilters}
    />
  )
}

export const IncomeListPageFilterBar = connect(
  (state: Store) => {
    const {
      dashboard: {
        common: { payment_methods: paymentMethodOptions, vat_areas: vatAreaOptions },
        tags: { data: tagOptions },
      },
      income: {
        revenueTypes: { data: revenueTypeOptions },
      },
      filters: { incomeList, isGlobalDateFilterDisabled },
    } = state
    return {
      revenueTypeOptions,
      filters: incomeList,
      isDateFilterDisabled: isGlobalDateFilterDisabled,
      paymentMethodOptions,
      tagOptions,
      vatAreaOptions,
    }
  },
  {
    resetFilters: filtersActions.resetIncomeListFilters.request,
    updateFilters: filtersActions.updateIncomeListFilters.request,
  }
)(PureIncomeListPageFilterBar)

IncomeListPageFilterBar.displayName = 'IncomeListPageFilterBar'
